import { PrintDisabled } from "@mui/icons-material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Dialog,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { CashModel } from "../../../data/models/cash/CashModel";
import {
  TypeDiscountEnum,
  TypePaymentMethodEnum,
} from "../../../data/models/order/PaymentModel";
import { currencyTransform, formatCurrency } from "../../../utils/StringTool";

export interface CheckoutDialogProps {
  open: boolean;
  onClose: () => void;
  subtotal: number;
  total: number;
  discountValue: number;
  discountType: TypeDiscountEnum;
  setDiscountValue: (v: number) => void;
  setDiscountType: (v: TypeDiscountEnum) => void;
  onConfirm: ({
    paymentMethod,
    reference,
    discountType,
    discountValue,
    cashId,
    withPrint,
  }: {
    paymentMethod: TypePaymentMethodEnum;
    reference?: string;
    discountType: TypeDiscountEnum;
    discountValue: number;
    cashId: string;
    withPrint?: boolean;
  }) => Promise<void>;
  discountRef: any;
  cashesOpened: CashModel[];
}

export function CheckoutDialog({
  open,
  onClose,
  subtotal,
  total,
  discountValue,
  discountType,
  setDiscountValue,
  setDiscountType,
  onConfirm,
  discountRef,
  cashesOpened,
}: CheckoutDialogProps) {
  const [payment, setPayment] = useState(TypePaymentMethodEnum.CASH);
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [reference, setReference] = useState("");
  const [openDiscount, setOpenDiscount] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedCashId, setSelectedCashId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const saved = localStorage.getItem("preferred_cash");
    if (saved && cashesOpened.some((c) => c.id === saved)) {
      setSelectedCashId(saved);
      return;
    }
    if (cashesOpened.length === 1) {
      setSelectedCashId(cashesOpened[0].id!);
    }
  }, [open]);

  const formatMoney = (v: number) =>
    Number(v).toLocaleString("es-CO", {
      minimumFractionDigits: 0,
    });

  const quickValues = [10, 100, 1000, 5000, 10000];

  const handleQuickAdd = (v: number) => {
    const newAmount = receivedAmount + v;
    setReceivedAmount(newAmount);
  };

  const change = Math.max(receivedAmount - total, 0);

  const handleEmit = async (withPrint: boolean = true) => {
    try {
      setLoading(true);
      await onConfirm({
        paymentMethod: payment,
        reference,
        discountType,
        discountValue,
        cashId: selectedCashId!,
        withPrint,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography fontWeight={700} fontSize={20}>
            Facturar orden
          </Typography>

          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintDisabled />}
            onClick={() => handleEmit(false)}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              py: 0.4,
              px: 1.2,
            }}
          >
            Facturar sin imprimir
          </Button>
        </Stack>

        <Typography fontWeight={600} sx={{ mb: 1 }}>
          Método de pago
        </Typography>
        <Stack direction="row" spacing={1} sx={{ width: "100%", mb: 2 }}>
          {[
            { label: "Efectivo", value: TypePaymentMethodEnum.CASH },
            { label: "Datáfono", value: TypePaymentMethodEnum.CARD },
            { label: "Transferencia", value: TypePaymentMethodEnum.TRANSFER },
          ].map((p) => (
            <Button
              key={p.value}
              variant={payment === p.value ? "contained" : "outlined"}
              onClick={() => setPayment(p.value)}
              sx={{
                flex: 1,
                textTransform: "uppercase",
                borderRadius: 2,
                py: 1.2,
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              {p.label}
            </Button>
          ))}
        </Stack>
        {cashesOpened.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography fontWeight={600} sx={{ mb: 1 }}>
              Seleccionar caja
            </Typography>

            <Grid container spacing={1}>
              {cashesOpened.map((cash) => (
                <Grid item xs={6} key={cash.id}>
                  <Button
                    fullWidth
                    variant={
                      selectedCashId === cash.id ? "contained" : "outlined"
                    }
                    onClick={() => {
                      setSelectedCashId(cash.id!);
                      localStorage.setItem("preferred_cash", cash.id!);
                    }}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      py: 1.2,
                      fontWeight: 600,
                    }}
                  >
                    {cash.name}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        <Box sx={{ mb: 2 }}>
          <Box ref={discountRef} sx={{ position: "relative", mb: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mt: 1,
                cursor: "pointer",
                p: 1,
                borderRadius: "10px",
                border: "1px solid",
                borderColor: "divider",
              }}
              onClick={() => setOpenDiscount(!openDiscount)}
            >
              <Typography fontWeight={600}>Descuento</Typography>
              <ExpandMore
                sx={{
                  transform: openDiscount ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "0.2s",
                }}
              />
            </Stack>
            {openDiscount && (
              <Stack spacing={1.5} sx={{ mt: 1.5 }} alignItems="flex-end">
                <Stack direction="row" spacing={1}>
                  <Button
                    variant={
                      discountType === TypeDiscountEnum.FLAT
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => setDiscountType(TypeDiscountEnum.FLAT)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px",
                      px: 2,
                    }}
                  >
                    Valor ($)
                  </Button>
                  <Button
                    variant={
                      discountType === TypeDiscountEnum.PERCENT
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => setDiscountType(TypeDiscountEnum.PERCENT)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px",
                      px: 2,
                    }}
                  >
                    %
                  </Button>
                </Stack>
                {discountType === TypeDiscountEnum.FLAT ? (
                  <TextField
                    size="small"
                    placeholder="$0"
                    value={discountValue}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, "");
                      setDiscountValue(Number(raw));
                    }}
                    InputProps={{
                      startAdornment: (
                        <Typography sx={{ mr: 1, opacity: 0.7 }}>$</Typography>
                      ),
                    }}
                    sx={{
                      width: 160,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                      },
                    }}
                  />
                ) : (
                  <TextField
                    size="small"
                    placeholder="0%"
                    value={discountValue}
                    onChange={(e) => {
                      let val = Number(e.target.value.replace(/\D/g, ""));
                      if (val > 100) val = 100;
                      setDiscountValue(val);
                    }}
                    InputProps={{
                      endAdornment: (
                        <Typography sx={{ ml: 1, opacity: 0.7 }}>%</Typography>
                      ),
                    }}
                    sx={{
                      width: 160,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                      },
                    }}
                  />
                )}
              </Stack>
            )}
          </Box>
          {payment === "CASH" ? (
            <Box sx={{ mb: 2 }}>
              <Typography fontWeight={600} sx={{ mb: 1 }}>
                Monto recibido
              </Typography>
              <TextField
                fullWidth
                size="small"
                autoFocus
                value={`${formatCurrency(receivedAmount)}`}
                onChange={(e) => {
                  const clean = currencyTransform(e.target.value);
                  setReceivedAmount(clean);
                }}
                sx={{ mb: 2 }}
              />
              <Grid container spacing={1.2} sx={{ mt: 1, mb: 2 }}>
                {quickValues.map((v) => (
                  <Grid item xs={4} key={v}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => handleQuickAdd(v)}
                      sx={{
                        borderRadius: "12px",
                        py: 1.2,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        borderColor: "rgba(0, 180, 100, 0.7)",
                        color: "rgba(0, 180, 100, 1)",
                        backgroundColor: "rgba(0, 180, 100, 0.05)",
                        "&:hover": {
                          borderColor: "rgba(0, 200, 120, 1)",
                          backgroundColor: "rgba(0, 255, 150, 0.12)",
                        },
                        "&:active": {
                          transform: "scale(0.97)",
                        },
                      }}
                    >
                      +${formatMoney(v)}
                    </Button>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    onClick={() => setReceivedAmount(0)}
                    sx={{
                      borderRadius: "12px",
                      py: 1.1,
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#ff4747",
                      backgroundColor: "rgba(255, 71, 71, 0.08)",
                      border: "1px solid rgba(255, 71, 71, 0.40)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 71, 71, 0.15)",
                        borderColor: "rgba(255, 71, 71, 0.7)",
                      },
                      "&:active": {
                        transform: "scale(0.97)",
                      },
                    }}
                  >
                    LIMPIAR MONTO
                  </Button>
                </Grid>
              </Grid>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 2 }}
              >
                <Typography fontWeight={600}>Devuelta:</Typography>
                <Typography fontWeight={700}>${formatMoney(change)}</Typography>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Typography fontWeight={600} sx={{ mb: 1 }}>
                Referencia
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </Box>
          )}
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
            <Typography fontWeight={600}>Subtotal:</Typography>
            <Typography fontWeight={600}>${formatMoney(subtotal)}</Typography>
          </Stack>
          {discountValue > 0 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 0.5 }}
            >
              <Typography fontWeight={600} color="text.secondary">
                Descuento:
              </Typography>

              <Typography fontWeight={600} color="text.secondary">
                {discountType === TypeDiscountEnum.FLAT
                  ? `-$${formatMoney(discountValue)}`
                  : `-${discountValue}% (${formatMoney(
                      Math.round((subtotal * discountValue) / 100)
                    )})`}
              </Typography>
            </Stack>
          )}
        </Box>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
          <Typography fontWeight={700} fontSize={20}>
            Total:
          </Typography>
          <Typography fontWeight={700} fontSize={20}>
            ${formatMoney(total)}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          fullWidth
          size="large"
          sx={{ mt: 3, borderRadius: 2, py: 1.5, fontWeight: 700 }}
          onClick={() => handleEmit(true)}
          loading={loading}
          disabled={!selectedCashId}
        >
          Emitir factura
        </Button>
      </Box>
    </Dialog>
  );
}
